/**
 * Game Session Manager - Tracks games played across the platform
 * Manages login prompts and guest game limits
 */

export interface GameSession {
  gameId: string;
  gameType: string;
  startTime: number;
  endTime?: number;
  completed: boolean;
}

export interface LoginPromptState {
  gamesPlayed: number;
  lastPromptShown: number;
  promptDismissedUntil?: number; // Timestamp when user can be prompted again
  permanentlyDismissed: boolean;
}

export class GameSessionManager {
  private static instance: GameSessionManager | null = null;
  private readonly STORAGE_KEY = 'playhub_game_sessions';
  private readonly LOGIN_PROMPT_KEY = 'playhub_login_prompt';
  private readonly GAMES_BEFORE_PROMPT = 3;
  private readonly POSTPONE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  static getInstance(): GameSessionManager {
    if (!GameSessionManager.instance) {
      GameSessionManager.instance = new GameSessionManager();
    }
    return GameSessionManager.instance;
  }

  /**
   * Start a new game session
   */
  startGameSession(gameType: string): string {
    const gameId = this.generateGameId();
    const session: GameSession = {
      gameId,
      gameType,
      startTime: Date.now(),
      completed: false
    };

    const sessions = this.getGameSessions();
    sessions.push(session);
    this.saveGameSessions(sessions);

    console.log(`🎮 Started ${gameType} session: ${gameId}`);
    return gameId;
  }

  /**
   * End a game session
   */
  endGameSession(gameId: string, completed: boolean = true): void {
    const sessions = this.getGameSessions();
    const sessionIndex = sessions.findIndex(s => s.gameId === gameId);
    
    if (sessionIndex !== -1) {
      sessions[sessionIndex].endTime = Date.now();
      sessions[sessionIndex].completed = completed;
      this.saveGameSessions(sessions);

      if (completed) {
        this.incrementCompletedGames();
        console.log(`🎮 Completed ${sessions[sessionIndex].gameType} session: ${gameId}`);
      }
    }
  }

  /**
   * Get total completed games count
   */
  getCompletedGamesCount(): number {
    const sessions = this.getGameSessions();
    return sessions.filter(s => s.completed).length;
  }

  /**
   * Get games played since last login prompt
   */
  getGamesSinceLastPrompt(): number {
    const promptState = this.getLoginPromptState();
    const totalGames = this.getCompletedGamesCount();
    return totalGames - (promptState.gamesPlayed || 0);
  }

  /**
   * Check if login prompt should be shown
   */
  shouldShowLoginPrompt(): boolean {
    const promptState = this.getLoginPromptState();
    const now = Date.now();

    // Don't show if permanently dismissed
    if (promptState.permanentlyDismissed) {
      return false;
    }

    // Don't show if postponed and postpone period hasn't expired
    if (promptState.promptDismissedUntil && now < promptState.promptDismissedUntil) {
      return false;
    }

    // Show if user has played 3 or more games since last prompt
    const gamesSincePrompt = this.getGamesSinceLastPrompt();
    return gamesSincePrompt >= this.GAMES_BEFORE_PROMPT;
  }

  /**
   * Mark login prompt as shown
   */
  markLoginPromptShown(): void {
    const promptState = this.getLoginPromptState();
    promptState.gamesPlayed = this.getCompletedGamesCount();
    promptState.lastPromptShown = Date.now();
    this.saveLoginPromptState(promptState);
  }

  /**
   * Postpone login prompt for later
   */
  postponeLoginPrompt(): void {
    const promptState = this.getLoginPromptState();
    promptState.promptDismissedUntil = Date.now() + this.POSTPONE_DURATION;
    promptState.gamesPlayed = this.getCompletedGamesCount();
    this.saveLoginPromptState(promptState);
    
    console.log('📅 Login prompt postponed for 24 hours');
  }

  /**
   * Permanently dismiss login prompt
   */
  permanentlyDismissLoginPrompt(): void {
    const promptState = this.getLoginPromptState();
    promptState.permanentlyDismissed = true;
    promptState.gamesPlayed = this.getCompletedGamesCount();
    this.saveLoginPromptState(promptState);
    
    console.log('🚫 Login prompt permanently dismissed');
  }

  /**
   * Reset login prompt state (for testing or after login)
   */
  resetLoginPromptState(): void {
    const promptState: LoginPromptState = {
      gamesPlayed: this.getCompletedGamesCount(),
      lastPromptShown: Date.now(),
      permanentlyDismissed: false
    };
    this.saveLoginPromptState(promptState);
  }

  /**
   * Get remaining free games before next prompt
   */
  getRemainingFreeGames(): number {
    const gamesSincePrompt = this.getGamesSinceLastPrompt();
    return Math.max(0, this.GAMES_BEFORE_PROMPT - gamesSincePrompt);
  }

  /**
   * Get game statistics
   */
  getGameStatistics() {
    const sessions = this.getGameSessions();
    const completedSessions = sessions.filter(s => s.completed);
    
    const stats = {
      totalGames: completedSessions.length,
      gamesByType: {} as Record<string, number>,
      averageSessionTime: 0,
      lastPlayedGame: null as GameSession | null
    };

    // Count games by type
    completedSessions.forEach(session => {
      stats.gamesByType[session.gameType] = (stats.gamesByType[session.gameType] || 0) + 1;
    });

    // Calculate average session time
    const sessionsWithTime = completedSessions.filter(s => s.endTime);
    if (sessionsWithTime.length > 0) {
      const totalTime = sessionsWithTime.reduce((sum, s) => sum + (s.endTime! - s.startTime), 0);
      stats.averageSessionTime = totalTime / sessionsWithTime.length;
    }

    // Get last played game
    if (completedSessions.length > 0) {
      stats.lastPlayedGame = completedSessions[completedSessions.length - 1];
    }

    return stats;
  }

  /**
   * Clean up old sessions (keep last 100)
   */
  cleanupOldSessions(): void {
    const sessions = this.getGameSessions();
    if (sessions.length > 100) {
      const recentSessions = sessions.slice(-100);
      this.saveGameSessions(recentSessions);
      console.log(`🧹 Cleaned up ${sessions.length - 100} old game sessions`);
    }
  }

  // Private methods
  private incrementCompletedGames(): void {
    // This is handled by endGameSession, but we could add additional logic here
    this.cleanupOldSessions();
  }

  private getGameSessions(): GameSession[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading game sessions:', error);
      return [];
    }
  }

  private saveGameSessions(sessions: GameSession[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessions));
    } catch (error) {
      console.error('Error saving game sessions:', error);
    }
  }

  private getLoginPromptState(): LoginPromptState {
    try {
      const stored = localStorage.getItem(this.LOGIN_PROMPT_KEY);
      return stored ? JSON.parse(stored) : {
        gamesPlayed: 0,
        lastPromptShown: 0,
        permanentlyDismissed: false
      };
    } catch (error) {
      console.error('Error loading login prompt state:', error);
      return {
        gamesPlayed: 0,
        lastPromptShown: 0,
        permanentlyDismissed: false
      };
    }
  }

  private saveLoginPromptState(state: LoginPromptState): void {
    try {
      localStorage.setItem(this.LOGIN_PROMPT_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving login prompt state:', error);
    }
  }

  private generateGameId(): string {
    return `game_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
}